import { useState } from "react";
import {
  ORDER_BOARD_STAGE_PREFIX,
  type OrderBoardStage,
  type OrderStatus,
} from "@school-sales-ai/shared";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  useCreateOrderBoardStage,
  useDeleteOrderBoardStage,
  useReorderOrderBoardStages,
  useUpdateOrderBoardStage,
} from "@/hooks/useOrderBoardStages";
import { ORDER_STATUS_META } from "@/lib/order-status-meta";
import { PIPELINE_COLOR_OPTIONS } from "@/lib/stage-colors";

type Props = {
  open: boolean;
  onClose: () => void;
  stages: OrderBoardStage[];
  /** Orders per column key — used to block deleting a non-empty column. */
  orderCounts: Record<string, number>;
};

function FixedRow({ status }: { status: OrderStatus }): React.JSX.Element {
  const meta = ORDER_STATUS_META[status];
  return (
    <div className="flex items-center gap-2 rounded-[12px] border border-ink-100 bg-white p-3">
      <span className={`h-2.5 w-2.5 rounded-full ${meta.swatch}`} aria-hidden />
      <span className="flex-1 text-sm font-medium text-ink-800">{meta.label}</span>
      <span className="rounded-full bg-ink-100 px-2 py-0.5 text-[10px] font-semibold text-ink-500">
        Fija
      </span>
    </div>
  );
}

/**
 * «Columnas» for the /pedidos board — `ManageColumnsModal`'s shape without the
 * reporting roles.
 *
 * The five statuses are fixed: the agent, the stock and the order lifecycle
 * depend on what each one means. What an admin adds are the steps BETWEEN
 * confirming and delivering — "Preparando", "En camino" — so the custom rows
 * are drawn nested under "Por entregar", which is where they live on the board.
 */
export function ManageOrderColumnsModal({
  open,
  onClose,
  stages,
  orderCounts,
}: Props): React.JSX.Element {
  const createStage = useCreateOrderBoardStage();
  const updateStage = useUpdateOrderBoardStage();
  const deleteStage = useDeleteOrderBoardStage();
  const reorderStages = useReorderOrderBoardStages();
  const [confirmingDeleteId, setConfirmingDeleteId] = useState<string | null>(null);

  const ordered = [...stages].sort((a, b) => a.position - b.position);

  function handleRename(stage: OrderBoardStage, value: string): void {
    const label = value.trim();
    if (!label || label === stage.label) return;
    updateStage.mutate({ id: stage.id, data: { label } });
  }

  function handleReorder(stageId: string, direction: -1 | 1): void {
    const idx = ordered.findIndex((s) => s.id === stageId);
    const target = idx + direction;
    if (idx < 0 || target < 0 || target >= ordered.length) return;
    const ids = ordered.map((s) => s.id);
    const moved = ids[idx];
    const swapped = ids[target];
    if (moved === undefined || swapped === undefined) return;
    ids[idx] = swapped;
    ids[target] = moved;
    reorderStages.mutate(ids);
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="flex max-h-[85vh] flex-col sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Administrar columnas</DialogTitle>
          <DialogDescription>
            Los estados del pedido son columnas fijas. Puedes agregar pasos propios
            dentro de <strong>Por entregar</strong> — por ejemplo «Preparando» o
            «En camino» — y renombrarlos, colorearlos, reordenarlos o eliminarlos.
            Solo puedes eliminar columnas vacías.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-2 overflow-y-auto pr-1">
          <FixedRow status="open" />
          <FixedRow status="confirmed" />

          <div className="ml-4 flex flex-col gap-2 border-l-2 border-ink-100 pl-3">
            {ordered.map((stage, position) => {
              const count = orderCounts[`${ORDER_BOARD_STAGE_PREFIX}${stage.id}`] ?? 0;
              return (
                <div
                  key={stage.id}
                  className="flex flex-col gap-2 rounded-[12px] border border-ink-100 bg-white p-3"
                >
                  <div className="flex items-center gap-2">
                    <input
                      key={`${stage.id}:${stage.label}`}
                      type="text"
                      aria-label="Nombre de la columna"
                      defaultValue={stage.label}
                      maxLength={64}
                      onBlur={(e) => handleRename(stage, e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") e.currentTarget.blur();
                      }}
                      className="flex-1 rounded-[8px] border border-ink-200 px-2 py-1 text-sm text-ink-800 focus:border-moss focus:outline-none"
                    />
                    <div className="flex items-center gap-0.5">
                      <button
                        type="button"
                        aria-label="Subir"
                        disabled={position <= 0 || reorderStages.isPending}
                        onClick={() => handleReorder(stage.id, -1)}
                        className="rounded p-1 text-ink-500 hover:bg-ink-050 disabled:opacity-30"
                      >
                        ▲
                      </button>
                      <button
                        type="button"
                        aria-label="Bajar"
                        disabled={position >= ordered.length - 1 || reorderStages.isPending}
                        onClick={() => handleReorder(stage.id, 1)}
                        className="rounded p-1 text-ink-500 hover:bg-ink-050 disabled:opacity-30"
                      >
                        ▼
                      </button>
                    </div>
                  </div>

                  <div className="flex flex-wrap items-center gap-3">
                    <div className="flex items-center gap-1">
                      {PIPELINE_COLOR_OPTIONS.map((c) => (
                        <button
                          key={c.name}
                          type="button"
                          aria-label={`Color ${c.name}`}
                          aria-pressed={stage.color === c.name}
                          onClick={() =>
                            updateStage.mutate({ id: stage.id, data: { color: c.name } })
                          }
                          className={`h-5 w-5 rounded-full ${c.swatch} ${
                            stage.color === c.name ? "ring-2 ring-ink-900 ring-offset-1" : ""
                          }`}
                        />
                      ))}
                    </div>

                    <div className="ml-auto flex items-center gap-2">
                      {confirmingDeleteId === stage.id ? (
                        <>
                          <span className="text-xs text-ink-600">¿Eliminar?</span>
                          <button
                            type="button"
                            disabled={deleteStage.isPending}
                            onClick={() =>
                              deleteStage.mutate(stage.id, {
                                onSuccess: () => setConfirmingDeleteId(null),
                              })
                            }
                            className="rounded-lg bg-red-600 px-2 py-1 text-xs font-medium text-white hover:bg-red-700 disabled:opacity-40"
                          >
                            Sí
                          </button>
                          <button
                            type="button"
                            onClick={() => setConfirmingDeleteId(null)}
                            className="rounded-lg border border-ink-200 px-2 py-1 text-xs font-medium text-ink-700 hover:bg-ink-050"
                          >
                            No
                          </button>
                        </>
                      ) : (
                        <button
                          type="button"
                          disabled={count > 0}
                          title={
                            count > 0
                              ? "No se puede eliminar: la columna tiene pedidos"
                              : "Eliminar columna"
                          }
                          onClick={() => setConfirmingDeleteId(stage.id)}
                          className="rounded-lg border border-red-200 px-2 py-1 text-xs font-medium text-red-600 hover:bg-red-50 disabled:cursor-not-allowed disabled:border-ink-100 disabled:text-ink-300"
                        >
                          Eliminar
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}

            <button
              type="button"
              disabled={createStage.isPending}
              onClick={() => createStage.mutate({ label: "Nueva columna" })}
              className="rounded-lg border border-dashed border-ink-300 py-2 text-sm font-medium text-ink-600 hover:bg-ink-050 disabled:opacity-40"
            >
              + Agregar columna
            </button>
          </div>

          <FixedRow status="delivered" />
          <FixedRow status="cancelled" />
          <FixedRow status="abandoned" />
        </div>
      </DialogContent>
    </Dialog>
  );
}
